import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { format, parseISO, differenceInDays, startOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowRight, HeartHandshake } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import type { FamilyAdminArea, FamilyAdminItem, FamilyAdminStatus } from '@/types';
import ActionPackCard from '@/components/ActionPackCard';
import FamilySchoolDeadlineSection from '@/components/family/FamilySchoolDeadlineSection';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppNativeSelect, AppPanelButton } from '../../../ui-foundation/adapters/web';

const AREA_OPTIONS: { value: FamilyAdminArea; label: string }[] = [
  { value: 'school', label: 'School' },
  { value: 'childcare', label: 'Childcare' },
  { value: 'co-parenting', label: 'Co-parenting' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'caring', label: 'Caring' },
  { value: 'other', label: 'Other' },
];

const STATUS_CLASSES: Record<FamilyAdminStatus, string> = {
  open: 'bg-mustard-light text-accent',
  waiting: 'bg-plum-light text-primary',
  done: 'bg-success-light text-success',
};

function formatStatus(status: FamilyAdminStatus) {
  if (status === 'waiting') return 'Waiting';
  if (status === 'done') return 'Done';
  return 'Open';
}

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

type FamilyAdminAccordionKey = 'school-deadlines' | 'family-admin' | 'family-support';

function FamilyAdminAccordion({
  emoji,
  title,
  subtitle: _subtitle,
  open,
  onToggle,
  children,
}: {
  emoji: string;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <SectionAccordionCard emoji={emoji} title={title} subtitle={_subtitle} open={open} onToggle={onToggle}>
      <div className="space-y-4">{children}</div>
    </SectionAccordionCard>
  );
}

export default function FamilyAdminTab({
  initialOpenSection,
  launchFamilyAdminItem = false,
  onLaunchHandled,
}: {
  initialOpenSection?: FamilyAdminAccordionKey;
  launchFamilyAdminItem?: boolean;
  onLaunchHandled?: () => void;
}) {
  const navigate = useNavigate();
  const { user, data, updateChildMaintenance } = useApp();
  const [items, setItems] = useState<FamilyAdminItem[]>([]);
  const [careReviewCount, setCareReviewCount] = useState(0);
  const [careReviewDueSoonCount, setCareReviewDueSoonCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [openSections, setOpenSections] = useState<Record<FamilyAdminAccordionKey, boolean>>({
    'school-deadlines': false,
    'family-admin': false,
    'family-support': false,
  });

  const [title, setTitle] = useState('');
  const [area, setArea] = useState<FamilyAdminArea>('school');
  const [childName, setChildName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const [maintenanceAmount, setMaintenanceAmount] = useState(String(data.childMaintenanceAmount || ''));
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(data.childMaintenanceSchedule || 'monthly');

  useEffect(() => {
    setMaintenanceAmount(data.childMaintenanceAmount ? String(data.childMaintenanceAmount) : '');
    setMaintenanceSchedule(data.childMaintenanceSchedule || 'monthly');
  }, [data.childMaintenanceAmount, data.childMaintenanceSchedule]);

  const fetchItems = useCallback(async () => {
    if (!user) return;
    const [{ data: result }, { data: careReviews }] = await Promise.all([
      supabase
        .from('family_admin_items' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('family_care_reviews' as any)
        .select('id, due_date, reminder_days_before, status')
        .eq('user_id', user.id),
    ]);

    setItems(((result || []) as any[]).map((item) => ({
      id: item.id,
      title: item.title,
      area: item.area,
      status: item.status,
      childName: item.child_name || '',
      dueDate: item.due_date,
      notes: item.notes || '',
      deadlineKind: item.deadline_kind || 'general',
      reminderDaysBefore: item.reminder_days_before ?? 14,
    })));
    const openCareReviews = ((careReviews || []) as any[]).filter((review) => review.status !== 'done');
    const careReviewDueSoon = openCareReviews.filter((review) =>
      differenceInDays(parseISO(review.due_date), startOfDay(new Date())) <= (review.reminder_days_before ?? 21)
    );
    setCareReviewCount(openCareReviews.length);
    setCareReviewDueSoonCount(careReviewDueSoon.length);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!initialOpenSection) return;
    setOpenSections((current) => current[initialOpenSection]
      ? current
      : { ...current, [initialOpenSection]: true });
  }, [initialOpenSection]);

  useEffect(() => {
    if (!launchFamilyAdminItem) {
      return;
    }

    setOpenSections((current) => ({
      ...current,
      'family-admin': true,
    }));
    setShowForm(true);
    onLaunchHandled?.();
  }, [launchFamilyAdminItem, onLaunchHandled]);

  const saveItem = async () => {
    if (!user || !title.trim()) return;
    setSaving(true);
    await supabase.from('family_admin_items' as any).insert({
      user_id: user.id,
      title: title.trim(),
      area,
      child_name: childName.trim() || null,
      due_date: dueDate || null,
      notes: notes.trim() || null,
      deadline_kind: 'general',
      reminder_days_before: 14,
    });
    setSaving(false);
    setShowForm(false);
    setTitle('');
    setArea('school');
    setChildName('');
    setDueDate('');
    setNotes('');
    notifySectionRefresh();
    void fetchItems();
  };

  const saveStarterItems = async (starterItems: Array<{
    title: string;
    area: FamilyAdminArea;
      notes?: string | null;
      child_name?: string | null;
      due_date?: string | null;
      deadline_kind?: string | null;
      reminder_days_before?: number | null;
  }>) => {
    if (!user) return;
    await supabase.from('family_admin_items' as any).insert(
      starterItems.map((item) => ({
        user_id: user.id,
        title: item.title,
        area: item.area,
        notes: item.notes || null,
        child_name: item.child_name || null,
        due_date: item.due_date || null,
        deadline_kind: item.deadline_kind || 'general',
        reminder_days_before: item.reminder_days_before ?? 14,
      })),
    );
    toast.success(`${starterItems.length} family task${starterItems.length === 1 ? '' : 's'} added.`);
    notifySectionRefresh();
    void fetchItems();
  };

  const updateStatus = async (itemId: string, status: FamilyAdminStatus) => {
    await supabase.from('family_admin_items' as any).update({ status }).eq('id', itemId);
    notifySectionRefresh();
    void fetchItems();
  };

  const saveSchoolDeadline = async ({
    title: deadlineTitle,
    childName: deadlineChildName,
    dueDate: deadlineDueDate,
    notes: deadlineNotes,
    deadlineKind,
    reminderDaysBefore,
  }: {
    title: string;
    childName: string;
    dueDate: string;
    notes: string;
    deadlineKind: string;
    reminderDaysBefore: number;
  }) => {
    if (!user) return;

    const { error } = await supabase.from('family_admin_items' as any).insert({
      user_id: user.id,
      title: deadlineTitle,
      area: 'school',
      child_name: deadlineChildName || null,
      due_date: deadlineDueDate,
      notes: deadlineNotes || null,
      deadline_kind: deadlineKind,
      reminder_days_before: reminderDaysBefore,
    });

    if (error) {
      throw error;
    }

    notifySectionRefresh();
    void fetchItems();
  };

  const saveMaintenance = async () => {
    setSavingMaintenance(true);
    await updateChildMaintenance(Number(maintenanceAmount || '0'), maintenanceSchedule);
    setSavingMaintenance(false);
    notifySectionRefresh();
  };

  const copyText = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(message);
  };

  const primaryChildName = data.profile.children.find((child) => child.name.trim())?.name?.trim() || '';

  const addSchoolPack = async () => {
    await saveStarterItems([
      {
        title: 'Confirm the next school or nursery date',
        area: 'school',
        child_name: primaryChildName || null,
        notes: 'Parents evening, trip consent, uniform, form, or payment.',
      },
      {
        title: 'Check the next childcare payment or booking',
        area: 'childcare',
        child_name: primaryChildName || null,
        notes: 'Keep provider dates, booking windows, and payment follow-up together.',
      },
    ]);
  };

  const addCoParentingPack = async () => {
    await saveStarterItems([
      {
        title: 'Record the next handover or shared-parenting change',
        area: 'co-parenting',
        child_name: primaryChildName || null,
        notes: 'Keep schedule changes and decisions in writing.',
      },
      {
        title: 'Track the next maintenance check-in or payment',
        area: 'maintenance',
        child_name: primaryChildName || null,
        notes: 'Use one task to hold what is due, what was agreed, and what needs following up.',
      },
    ]);
  };

  const addCaringPack = async () => {
    await saveStarterItems([
      {
        title: 'Log the next care appointment or review',
        area: 'caring',
        notes: 'Appointments, meds, forms, services, and next calls belong here.',
      },
      {
        title: 'Write down the next help request',
        area: 'caring',
        notes: 'One ask for support is easier to act on than carrying the whole plan alone.',
      },
    ]);
  };

  const stats = useMemo(() => {
    const openItems = items.filter((item) => item.status !== 'done');
    const dueSoon = openItems.filter((item) => {
      if (!item.dueDate) return false;
      return differenceInDays(parseISO(item.dueDate), startOfDay(new Date())) <= (item.reminderDaysBefore ?? 14);
    });
    const caringLoad = openItems.filter((item) => item.area === 'caring').length;

    return {
      open: openItems.length,
      dueSoon: dueSoon.length + careReviewDueSoonCount,
      caringLoad: caringLoad + careReviewCount,
    };
  }, [careReviewCount, careReviewDueSoonCount, items]);
  const toggleSection = useCallback((section: FamilyAdminAccordionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);

  if (loading) {
    return (
      <AppLoadingState
        title="Loading family admin"
        body="Pulling school, childcare, co-parenting, maintenance, and caring tasks."
      />
    );
  }

  return (
    <div className="space-y-4">
      <FamilyAdminAccordion
        emoji="🎒"
        title="School deadline tracker"
        subtitle="Forms, parents’ evenings, trips, and school-place deadlines in one place."
        open={openSections['school-deadlines']}
        onToggle={() => toggleSection('school-deadlines')}
      >
          <FamilySchoolDeadlineSection
            children={data.profile.children}
            items={items}
            onSaveDeadline={saveSchoolDeadline}
            onUpdateStatus={updateStatus}
          />
      </FamilyAdminAccordion>

      <FamilyAdminAccordion
        emoji="📋"
        title="Family admin"
        subtitle="School, childcare, co-parenting, maintenance, and caring tasks."
        open={openSections['family-admin']}
        onToggle={() => toggleSection('family-admin')}
      >
          <div className="grid grid-cols-3 gap-2">
            <MetricCard label="Open" value={stats.open} />
            <MetricCard label="Due soon" value={stats.dueSoon} />
            <MetricCard label="Caring" value={stats.caringLoad} />
          </div>
          <AppCard>
            <AppSectionHeader
              title="Family admin board"
              subtitle="Keep school, childcare, co-parenting, maintenance, and caring tasks in one list."
              action={(
                <AppButton size="sm" onPress={() => setShowForm((current) => !current)}>
                  <Plus size={12} />
                  {showForm ? 'Cancel' : 'Add'}
                </AppButton>
              )}
            />

            <div className="flex flex-wrap gap-2 mt-4">
              {AREA_OPTIONS.map((option) => {
                const count = items.filter((item) => item.area === option.value && item.status !== 'done').length;
                return (
                  <span key={option.value} className="rounded-full bg-background border border-border px-3 py-1.5 text-[11px] font-medium text-foreground">
                    {option.label} {count > 0 ? `· ${count}` : ''}
                  </span>
                );
              })}
            </div>

            {showForm && (
              <AppCard backgroundColor="$mutedBackground" className="mt-4 animate-fade-in">
                <AppFieldGroup label="Task title">
                  <AppInput
                    tag="input"
                    value={title}
                    onChange={(event) => setTitle((event.target as HTMLInputElement).value)}
                    placeholder="Parents' evening, nursery payment, care review…"
                  />
                </AppFieldGroup>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <AppFieldGroup label="Area">
                    <AppNativeSelect
                      value={area}
                      onChange={(event) => setArea(event.target.value as FamilyAdminArea)}
                    >
                      {AREA_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </AppNativeSelect>
                  </AppFieldGroup>
                  <AppFieldGroup label="Child / person">
                    <AppInput
                      tag="input"
                      value={childName}
                      onChange={(event) => setChildName((event.target as HTMLInputElement).value)}
                      placeholder="Optional"
                    />
                  </AppFieldGroup>
                </div>
                <AppDateField label="Due date" value={dueDate} onChange={setDueDate} />
                <AppFieldGroup label="Notes">
                  <AppTextArea
                    value={notes}
                    onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                    rows={3}
                    placeholder="What needs doing next?"
                  />
                </AppFieldGroup>
                <AppButton onPress={saveItem} disabled={!title.trim() || saving}>
                  {saving ? 'Saving…' : 'Save admin item'}
                </AppButton>
              </AppCard>
            )}

            <div className="space-y-3 mt-4">
              {items.length === 0 ? (
                <AppEmptyState
                  title="No family admin items yet"
                  body="Start with the next date, payment, or care task that is easiest to forget."
                />
              ) : (
                items.map((item) => (
                  <AppCard key={item.id} backgroundColor="$background">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <AppText fontSize="$sm" fontWeight="700">{item.title}</AppText>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_CLASSES[item.status]}`}>
                            {formatStatus(item.status)}
                          </span>
                        </div>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {AREA_OPTIONS.find((option) => option.value === item.area)?.label}
                          {item.childName ? ` · ${item.childName}` : ''}
                          {item.dueDate ? ` · ${format(parseISO(item.dueDate), 'd MMM yyyy')}` : ''}
                        </AppText>
                        {item.notes && (
                          <AppText className="mt-2" fontSize="$sm" color="$colorMuted">{item.notes}</AppText>
                        )}
                      </div>
                    </div>
                    <AppButtonRow mt="$sm">
                      {item.status !== 'waiting' && item.status !== 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'waiting')}>
                          Mark waiting
                        </AppButton>
                      )}
                      {item.status !== 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'done')}>
                          Mark done
                        </AppButton>
                      )}
                      {item.status === 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'open')}>
                          Reopen
                        </AppButton>
                      )}
                    </AppButtonRow>
                  </AppCard>
                ))
              )}
            </div>
          </AppCard>

          <AppCard>
            <AppSectionHeader
              title="Child maintenance"
              subtitle="Keep the recurring amount visible here if maintenance is part of your month-to-month admin."
              action={<HeartHandshake size={16} className="shrink-0 text-muted-foreground mt-1" />}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-4">
              <AppFieldGroup label="Amount (£)">
                <AppInput
                  tag="input"
                  type="number"
                  value={maintenanceAmount}
                  onChange={(event) => setMaintenanceAmount((event.target as HTMLInputElement).value)}
                />
              </AppFieldGroup>
              <AppFieldGroup label="Schedule">
                <AppNativeSelect
                  value={maintenanceSchedule}
                  onChange={(event) => setMaintenanceSchedule(event.target.value)}
                >
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                  <option value="four-weekly">Four-weekly</option>
                  <option value="other">Other</option>
                </AppNativeSelect>
              </AppFieldGroup>
            </div>

            <AppButtonRow mt="$md">
              <AppButton size="sm" onPress={saveMaintenance} disabled={savingMaintenance}>
                {savingMaintenance ? 'Saving…' : 'Save maintenance'}
              </AppButton>
              <AppButton size="sm" tone="outline" onPress={() => navigate('/profile')}>
                Open profile
              </AppButton>
            </AppButtonRow>
          </AppCard>
      </FamilyAdminAccordion>

      <FamilyAdminAccordion
        emoji="🫶"
        title="Family support"
        subtitle="Starter packs, scripts, and guides for when the load gets messy."
        open={openSections['family-support']}
        onToggle={() => toggleSection('family-support')}
      >
          <AppCard>
            <AppSectionHeader
              title="Family setup lives in My Settings"
              subtitle="Partner linking, children, pets, and detailed family records are set up in My Settings first. Family is for using the childcare, school, care, and admin tools once that setup is already in place."
            />
            <AppButton tone="outline" size="sm" onPress={() => navigate('/profile')}>
              Open My Settings
            </AppButton>
          </AppCard>

          <div className="grid gap-3">
            <ActionPackCard
              icon="🎒"
              eyebrow="School & Childcare"
              title="Keep the monthly family admin rhythm visible"
              description="Use the board for the things that get forgotten because they look small until they stack up: forms, bookings, payments, and dates."
              steps={[
                'Log the next school or nursery date before it disappears into messages.',
                'Track the next childcare payment or booking deadline in the same place.',
                'Keep one written message ready so you are not rewriting it from scratch each time.',
              ]}
              actions={[
                { label: 'Add school/admin pack', onClick: () => void addSchoolPack(), primary: true },
                {
                  label: 'Copy school message',
                  onClick: () => void copyText(
                    `Hi,\n\nI’m checking the next date / form / payment we need to deal with for ${primaryChildName || '[child name]'}. Please can you confirm what is outstanding and the deadline.\n\nThanks`,
                    'School message copied.',
                  ),
                },
              ]}
            />

            <ActionPackCard
              icon="🤝"
              eyebrow="Co-parenting & Maintenance"
              title="Keep difficult family admin factual and written down"
              description="When the load is emotional, the admin needs to get simpler. Keep dates, handovers, and money in one written trail."
              steps={[
                'Record the next handover, schedule change, or decision that needs confirming.',
                'Keep child maintenance amount and schedule visible in the same section.',
                'Send short written summaries instead of relying on memory after hard conversations.',
              ]}
              actions={[
                { label: 'Add co-parenting pack', onClick: () => void addCoParentingPack(), primary: true },
                {
                  label: 'Copy neutral summary',
                  onClick: () => void copyText(
                    `Hi,\n\nJust confirming what I understand from today:\n- [date / arrangement]\n- [child-related practical point]\n- [payment / follow-up if relevant]\n\nIf anything above is wrong, reply with the correction so we both have the same record.\n\nThanks`,
                    'Neutral summary copied.',
                  ),
                },
              ]}
            />

            <ActionPackCard
              icon="🫶"
              eyebrow="Caring Pack"
              title="Do not carry caring admin only in your head"
              description="Use Adulting to hold the next review, service call, or medication/admin task so caring work stays visible and easier to share."
              steps={[
                'Capture the next care appointment, review, or service call.',
                'Write down the one practical follow-up someone else could help with.',
                'Use guides when care stress starts bleeding into everything else.',
              ]}
              actions={[
                { label: 'Add caring pack', onClick: () => void addCaringPack(), primary: true },
                { label: 'Open support hub', onClick: () => navigate('/guides?section=family') },
              ]}
            />

            <ActionPackCard
              icon="⚖️"
              eyebrow="Carer Rights"
              title="Keep care reviews and carer rights in writing"
              description="Use one written request for an assessment or review instead of carrying the whole care system in your head."
              steps={[
                'Ask for a carers assessment or care-plan review in writing when the support no longer fits.',
                'Keep the next review date, medication review, or power-of-attorney admin visible here.',
                'Use the Family guides when needs change quickly or funding decisions stop making sense.',
              ]}
              actions={[
                {
                  label: 'Copy assessment request',
                  onClick: () => void copyText(
                    `Hello,\n\nI am supporting [person name] and I would like to request a carer's assessment / care review because the current support needs have changed. Please confirm the next step, what information you need from me, and when this can be arranged.\n\nThank you`,
                    'Assessment request copied.',
                  ),
                  primary: true,
                },
                {
                  label: 'Copy review request',
                  onClick: () => void copyText(
                    `Hello,\n\nI am writing to ask for a review of the current care arrangement for [person name]. The practical concerns I need reviewed are:\n- [what has changed]\n- [what support is not working]\n- [what risk or pressure is growing]\n\nPlease confirm the review process and the next available date.\n\nThank you`,
                    'Review request copied.',
                  ),
                },
                { label: 'Open support hub', onClick: () => navigate('/guides?section=family') },
              ]}
            />
          </div>

          <QuickAction
            title="Family guides and playbooks"
            body="Use the Family guides for separation, school admin, and the parts of care that are easiest to keep carrying alone."
            onClick={() => navigate('/guides?section=family')}
          />
      </FamilyAdminAccordion>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <AppCard px="$sm" py="$sm">
      <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">
        {value}
      </AppText>
    </AppCard>
  );
}

function QuickAction({
  title,
  body,
  onClick,
}: {
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <AppPanelButton
      type="button"
      onClick={onClick}
      className="text-left"
    >
      <AppCard>
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <AppText fontSize="$sm" fontWeight="700">{title}</AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">{body}</AppText>
          </div>
          <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
        </div>
      </AppCard>
    </AppPanelButton>
  );
}
